import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { desktop, navigateTo } from "@/desktop/bridge";
import { useDiscoverLive } from "@/lib/queries";

/**
 * "Me avise" da Órbita vazia: quando ninguém está online, a pessoa pede um aviso e o app (que fica
 * na bandeja) acompanha a faixa viva; quando alguém fica online, avisa uma vez (balão e notificação
 * do sistema) e desliga sozinho.
 */

const KEY = "kompil.discover.avise";

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function write(on: boolean) {
  try {
    if (on) localStorage.setItem(KEY, "1");
    else localStorage.removeItem(KEY);
  } catch {
    // sem armazenamento: o aviso vale só enquanto o app estiver aberto
  }
}

export const useOrbitWatch = create<{ on: boolean; set: (on: boolean) => void }>((set) => ({
  on: read(),
  set: (on) => {
    write(on);
    set({ on });
    if (on && typeof Notification !== "undefined" && Notification.permission === "default")
      void Notification.requestPermission().catch(() => undefined);
  },
}));

/** Fica de olho na faixa viva enquanto o aviso estiver ligado (montado no app inteiro). */
export function useOrbitWatcher(enabled: boolean) {
  const on = useOrbitWatch((s) => s.on);
  const off = useOrbitWatch((s) => s.set);
  const active = enabled && on;
  const { data } = useDiscoverLive(active);
  const fired = useRef(false);
  useEffect(() => {
    if (!active) {
      fired.current = false;
      return;
    }
    const first = data?.online[0];
    if (!data || !data.counts.online || !first || fired.current) return;
    fired.current = true;
    off(false);
    const others = data.counts.online - 1;
    const text = `${first.name.split(" ")[0]}${others > 0 ? ` e mais ${others}` : ""} ${others > 0 ? "estão" : "está"} online na sua órbita`;
    toast(text, {
      duration: 12_000,
      action: { label: "Ver", onClick: () => navigateTo("/discover") },
    });
    const System = globalThis.Notification;
    if (typeof System !== "undefined" && System.permission === "granted" && !document.hasFocus()) {
      try {
        const n = new System("Kompil", { body: text, icon: "/brand/icon-192.png" });
        n.onclick = () => {
          if (desktop) desktop.focus();
          else window.focus();
          navigateTo("/discover");
        };
      } catch {
        // navegador que só notifica por service worker
      }
    }
  }, [active, data, off]);
}
